import { useCallback, useMemo } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import { SELECT_COLUMN_ID } from "constants/Constants";
import usePageTitle from "hooks/usePageTitle";
import TableIconBadge from "routes/custom_tables/TableIconBadge";
import { EVENT_DATASET_COLUMN } from "routes/meta_datasets/swoogo_events/eventDatasetColumn";
import useCanSetEventDataset from "routes/meta_datasets/swoogo_events/useCanSetEventDataset";
import EventActions from "routes/swoogo/EventActions";
import EventBulkActions from "routes/swoogo/EventBulkActions";
import { linkCell } from "routes/swoogo/linkCell";
import { swoogoEventKeys } from "routes/swoogo/queryKeys";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";
import SwoogoService from "services/Swoogo";

const HEADER = "Swoogo Events";

const eventCell = linkCell(({ id }) => `/swoogo/events/${id}`);

const COLUMNS = [
  {
    id: SELECT_COLUMN_ID,
    size: 60,
    enableSorting: false,
    meta: { isDisplay: true },
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onCheckedChange={checked => table.toggleAllRowsSelected(!!checked)}
        aria-label="Select all rows"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected() && !row.getIsSelected()}
        onCheckedChange={checked => row.toggleSelected(!!checked)}
        aria-label="Select row"
      />
    ),
  },
  { id: "ID", accessorKey: "id", header: "ID", size: 90, cell: eventCell },
  {
    id: "Name",
    accessorKey: "name",
    header: "Name",
    size: 280,
    cell: eventCell,
  },
  { id: "Status", accessorKey: "status", header: "Status", size: 120 },
  {
    id: "Folder",
    accessorKey: "folder_path",
    sortKey: "folder_id",
    header: "Folder",
    size: 240,
    cell: ({ row }) => row.original.folder_path || "—",
  },
  { id: "Start Date", accessorKey: "start_date", header: "Start Date" },
  { id: "End Date", accessorKey: "end_date", header: "End Date" },
  {
    id: "Start Time",
    accessorKey: "start_time",
    header: "Start Time",
    enableSorting: false,
  },
  {
    id: "End Time",
    accessorKey: "end_time",
    header: "End Time",
    enableSorting: false,
  },
];

const fetchFolderOptions = async ({ search_query }) => {
  const { data } = await SwoogoService.listEventFolders({
    search: search_query,
    per_page: 200,
  });
  return data;
};

// Swoogo documents no list of event statuses, so the options are the stored ones.
const fetchStatusOptions = async () => {
  const statuses = await SwoogoService.listEventStatuses();
  return statuses.map(status => ({ id: status, name: status }));
};

const FILTERS = [
  {
    key: "folder_id",
    title: "Folder",
    type: "select",
    queryFn: fetchFolderOptions,
  },
  {
    key: "status",
    title: "Status",
    type: "select",
    queryFn: fetchStatusOptions,
  },
];

const EventsTable = () => {
  usePageTitle(HEADER);

  const { lastSyncedAt } = useSwoogoConnected();
  const canSetDataset = useCanSetEventDataset();

  const queryKey = useMemo(
    () => swoogoEventKeys.list(lastSyncedAt),
    [lastSyncedAt]
  );

  const columns = useMemo(() => {
    const actionsColumn = {
      header: <div />,
      id: "Actions",
      accessorKey: "actions",
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: { isDisplay: true },
      cell: ({ row }) => (
        <EventActions eventId={row.original.id} canSetDataset={canSetDataset} />
      ),
    };
    return [
      ...COLUMNS,
      ...(canSetDataset ? [EVENT_DATASET_COLUMN] : []),
      actionsColumn,
    ];
  }, [canSetDataset]);

  const fetchEvents = useCallback(
    async queryParams =>
      SwoogoService.listEvents(getNextQueryParams(queryParams, columns)),
    [columns]
  );

  const renderBulkActions = useCallback(
    ({ selectedIds, uncheckAll }) => (
      <EventBulkActions selectedIds={selectedIds} uncheckAll={uncheckAll} />
    ),
    []
  );

  return (
    <BaseTableV3
      columns={columns}
      headerIcon={<TableIconBadge iconName="calendar-days" />}
      header={HEADER}
      tableName="swoogo_events"
      queryKey={queryKey}
      fetchData={fetchEvents}
      filters={FILTERS}
      actionButtons={canSetDataset ? renderBulkActions : undefined}
      enableSearch
      enableSettings
    />
  );
};

export default EventsTable;
